import { COMPILED_COLLISION_CELLS_PER_TILE, EMPTY_TILE, frameId, type AssetManifest, type CompiledChunk } from "daedalus-core";
import { HOLE, SOLID, WATER, groundSurface, type PuddleGround } from "odyssey-shared";
import type { GroundSurfaces } from "../ground-surfaces.ts";

/**
 * What the puddles of a map are placed on, read from the booted world (P2.27-T20): which tiles are soft or hard ground by
 * their surface, and which collision cells are blocked by a collision flag, a prop's art, or a tile layer in or above the
 * players band that names no ground (a fence, a wall, a roof; a terrain is drawn flat even there), so a puddle never lies under or against anything that stands.
 */

const SOFT_SURFACES: ReadonlySet<string> = new Set(["grass"]);
const HARD_SURFACES: ReadonlySet<string> = new Set(["dirt", "stone", "sand"]);
const STOPPING = SOLID | WATER | HOLE;

/**
 * What the world gives: the map's size, its chunks, the surfaces, the collision cells as compiled, the asset manifest and the
 * players band.
 */
export interface PuddleWorldInput {
  readonly tileSize: number;
  readonly chunkSize: number;
  readonly width: number;
  readonly height: number;
  readonly chunks: ReadonlyMap<string, CompiledChunk>;
  readonly surfaces: GroundSurfaces;
  readonly collision: Uint8Array;
  readonly assets: AssetManifest;
  readonly band: number;
}

/**
 * Marks a rectangle of world pixels as blocked.
 *
 * @param blocked The blocked cells.
 * @param columns Cells across.
 * @param rows Cells down.
 * @param cellPx A cell's width in pixels.
 * @param left Left edge in pixels.
 * @param top Top edge.
 * @param right Right edge.
 * @param bottom Bottom edge.
 */
function blockRect(blocked: Uint8Array, columns: number, rows: number, cellPx: number, left: number, top: number, right: number, bottom: number): void {
  const x0 = Math.max(0, Math.floor(left / cellPx));
  const x1 = Math.min(columns - 1, Math.floor((right - 1) / cellPx));
  const y0 = Math.max(0, Math.floor(top / cellPx));
  const y1 = Math.min(rows - 1, Math.floor((bottom - 1) / cellPx));
  for (let y = y0; y <= y1; y += 1) {
    blocked.fill(1, y * columns + x0, y * columns + x1 + 1);
  }
}

/**
 * Reads the ground puddles are placed on.
 *
 * @param input The booted world's parts.
 * @returns The ground and its blocked cells.
 */
export function puddleGroundOf(input: PuddleWorldInput): PuddleGround {
  const { width, height, tileSize, chunkSize } = input;
  const per = COMPILED_COLLISION_CELLS_PER_TILE;
  const columns = width * per;
  const rows = height * per;
  const cellPx = tileSize / per;
  const ground = new Uint8Array(width * height);
  const names = input.surfaces.names;
  for (let i = 0; i < ground.length; i += 1) {
    const value = input.surfaces.cells[i] as number;
    const name = value === 0 ? "" : (names[value - 1] ?? "");
    ground[i] = SOFT_SURFACES.has(name) ? 1 : HARD_SURFACES.has(name) ? 2 : 0;
  }
  const blocked = new Uint8Array(columns * rows);
  for (let i = 0; i < blocked.length; i += 1) {
    if (((input.collision[i] as number) & STOPPING) !== 0) blocked[i] = 1;
  }
  for (const chunk of input.chunks.values()) {
    for (let index = 0; index < chunk.layers.length; index += 1) {
      const layer = chunk.layers[index];
      if (layer === undefined) continue;
      if (layer.kind === "tile") {
        if (index < input.band || groundSurface(layer.name) !== null) continue;
        for (let t = 0; t < layer.tiles.length; t += 1) {
          if (layer.tiles[t] === EMPTY_TILE) continue;
          const tx = chunk.cx * chunkSize + (t % chunkSize);
          const ty = chunk.cy * chunkSize + Math.floor(t / chunkSize);
          if (tx < width && ty < height) blockRect(blocked, columns, rows, cellPx, tx * tileSize, ty * tileSize, (tx + 1) * tileSize, (ty + 1) * tileSize);
        }
      } else if (layer.kind === "prop") {
        for (const prop of layer.props) {
          const rect = input.assets.frames[frameId(prop.frame.tileset, null, prop.frame.tile)];
          if (rect === undefined || rect.empty === true || rect.ground === true) continue;
          const left = prop.x * tileSize;
          const bottom = (prop.y + 1) * tileSize;
          const artX = rect.art?.x ?? 0;
          const artW = rect.art?.w ?? rect.w;
          const artY = rect.art?.y ?? 0;
          const x0 = prop.flipX ? rect.w - artX - artW : artX;
          const top = bottom - rect.h;
          blockRect(blocked, columns, rows, cellPx, left + x0, top + artY, left + x0 + artW, top + rect.base);
        }
      }
    }
  }
  return { tileSize, cellsPerTile: per, width, height, ground, blocked };
}

/**
 * Whether a world point is blocked for a puddle on a map's ground: outside the map, or on a collision cell where a
 * character is stopped or a sprite or wall stands (`PuddleGround.blocked`). The drip puddles keep clear of it as the
 * rain's puddles do.
 *
 * @param ground The map's puddle ground.
 * @returns The test, for world pixels.
 */
export function puddleBlockedAt(ground: PuddleGround): (x: number, y: number) => boolean {
  const cell = ground.tileSize / ground.cellsPerTile;
  const columns = ground.width * ground.cellsPerTile;
  const rows = ground.height * ground.cellsPerTile;
  return (x, y) => {
    const column = Math.floor(x / cell);
    const row = Math.floor(y / cell);
    return column < 0 || row < 0 || column >= columns || row >= rows || ground.blocked[row * columns + column] !== 0;
  };
}
